import { z } from 'zod';

/**
 * Canonical storage units. Every quantity is persisted in these units as an integer
 * so arithmetic, summaries and exports never depend on per-row unit columns.
 *
 * - volume: millilitres (ml)
 * - mass: grams (g)
 * - length: millimetres (mm)
 */

export const volumeDisplayUnitSchema = z.enum(['ml', 'oz']);
export type VolumeDisplayUnit = z.infer<typeof volumeDisplayUnitSchema>;

export const massDisplayUnitSchema = z.enum(['kg', 'lb']);
export type MassDisplayUnit = z.infer<typeof massDisplayUnitSchema>;

export const lengthDisplayUnitSchema = z.enum(['cm', 'in']);
export type LengthDisplayUnit = z.infer<typeof lengthDisplayUnitSchema>;

/** Non-negative integer millilitres. */
export const millilitresSchema = z.number().int().nonnegative().max(10_000);
/** Combined pump volume, allowing both sides at the per-side maximum. */
export const pumpTotalSchema = z.number().int().nonnegative().max(20_000);
/** Elapsed time stored on an activity, capped at 48 hours. */
export const durationSecondsSchema = z.number().int().nonnegative().max(172_800);
/** Non-negative integer grams. */
export const gramsSchema = z.number().int().nonnegative().max(100_000);
/** Non-negative integer millimetres. */
export const millimetresSchema = z.number().int().nonnegative().max(3_000);
/**
 * Dose amount in minor units: millilitres and tablets in tenths, milligrams and drops as whole units.
 * 1.5 ml is 15. A half tablet is 5.
 */
export const doseAmountSchema = z.number().int().nonnegative().max(100_000);

const ML_PER_US_FL_OZ = 29.5735295625;
const G_PER_LB = 453.59237;
const MM_PER_IN = 25.4;

function roundToInt(value: number): number {
    return Math.round(value);
}

function roundTo(value: number, decimals: number): number {
    const factor = 10 ** decimals;
    return Math.round(value * factor) / factor;
}

export function volumeToMillilitres(value: number, unit: VolumeDisplayUnit): number {
    switch (unit) {
        case 'ml':
            return roundToInt(value);
        case 'oz':
            return roundToInt(value * ML_PER_US_FL_OZ);
    }
}

export function millilitresToVolume(ml: number, unit: VolumeDisplayUnit): number {
    switch (unit) {
        case 'ml':
            return ml;
        case 'oz':
            return roundTo(ml / ML_PER_US_FL_OZ, 2);
    }
}

export function massToGrams(value: number, unit: MassDisplayUnit): number {
    switch (unit) {
        case 'kg':
            return roundToInt(value * 1000);
        case 'lb':
            return roundToInt(value * G_PER_LB);
    }
}

export function gramsToMass(grams: number, unit: MassDisplayUnit): number {
    switch (unit) {
        case 'kg':
            return roundTo(grams / 1000, 3);
        case 'lb':
            return roundTo(grams / G_PER_LB, 2);
    }
}

export function lengthToMillimetres(value: number, unit: LengthDisplayUnit): number {
    switch (unit) {
        case 'cm':
            return roundToInt(value * 10);
        case 'in':
            return roundToInt(value * MM_PER_IN);
    }
}

export function millimetresToLength(mm: number, unit: LengthDisplayUnit): number {
    switch (unit) {
        case 'cm':
            return roundTo(mm / 10, 1);
        case 'in':
            return roundTo(mm / MM_PER_IN, 2);
    }
}

/**
 * Per-child display preferences. Storage is always canonical; these only affect presentation.
 */
export const unitPreferencesSchema = z.object({
    volume: volumeDisplayUnitSchema,
    mass: massDisplayUnitSchema,
    length: lengthDisplayUnitSchema,
});

export type UnitPreferences = z.infer<typeof unitPreferencesSchema>;

export const defaultUnitPreferences: UnitPreferences = {
    volume: 'ml',
    mass: 'kg',
    length: 'cm',
};
